export type Locale = "en" | "zh";
export type Localized = Record<Locale, string>;

export const ENDPOINT = "https://mcp.blockrazor.io/docs";

export const clients = [
  {
    id: "codex",
    name: "Codex",
    setupTitle: { en: "Add the server", zh: "添加服务" },
    code: "codex mcp add blockrazor-docs --url https://mcp.blockrazor.io/docs",
    note: {
      en: "Run this in a terminal where the Codex CLI is installed.",
      zh: "在已安装 Codex CLI 的终端中运行。",
    },
    verifyTitle: { en: "Verify the connection", zh: "验证连接" },
    verifyNote: {
      en: "List configured MCP servers and confirm `blockrazor-docs` appears.",
      zh: "列出已配置的 MCP 服务，并确认其中包含 `blockrazor-docs`。",
    },
    verifyCode: "codex mcp list",
    instructionFile: "AGENTS.md",
    showProjectInstructions: true,
  },
  {
    id: "claude",
    name: "Claude Code",
    setupTitle: { en: "Add the server", zh: "添加服务" },
    code: "claude mcp add --transport http blockrazor-docs https://mcp.blockrazor.io/docs",
    note: {
      en: "Claude Code supports remote HTTP MCP servers directly.",
      zh: "Claude Code 直接支持远程 HTTP MCP 服务。",
    },
    verifyTitle: { en: "Verify in Claude Code", zh: "在 Claude Code 中验证" },
    verifyNote: {
      en: "Open Claude Code and run `/mcp`. The server should list these tools:",
      zh: "打开 Claude Code 并运行 `/mcp`，服务中应列出以下工具：",
    },
    verifyCode: `askQuestion
searchDocumentation
getPage
sendFeedback`,
    instructionFile: "CLAUDE.md",
    showProjectInstructions: false,
  },
  {
    id: "cursor",
    name: "Cursor",
    setupTitle: { en: "Configure MCP", zh: "配置 MCP" },
    code: `{
  "mcpServers": {
    "blockrazor-docs": {
      "url": "https://mcp.blockrazor.io/docs"
    }
  }
}`,
    note: {
      en: "Add this to `~/.cursor/mcp.json` for global use, or `.cursor/mcp.json` inside one project.",
      zh: "全局使用时添加到 `~/.cursor/mcp.json`，单个项目使用时添加到项目内的 `.cursor/mcp.json`。",
    },
    verifyTitle: { en: "Verify in Agent chat", zh: "在 Agent 对话中验证" },
    verifyNote: {
      en: "Open Cursor Agent, check the available MCP tools, and ask it to search for the BlockRazor RPC integration guide.",
      zh: "打开 Cursor Agent，检查可用的 MCP 工具，并让它搜索 BlockRazor RPC 集成指南。",
    },
    verifyCode: null,
    instructionFile: ".cursor/rules/blockrazor-mcp.mdc",
    showProjectInstructions: false,
  },
  {
    id: "windsurf",
    name: "Windsurf",
    setupTitle: { en: "Configure MCP", zh: "配置 MCP" },
    code: `{
  "mcpServers": {
    "blockrazor-docs": {
      "serverUrl": "https://mcp.blockrazor.io/docs"
    }
  }
}`,
    note: {
      en: "Add this to `~/.codeium/windsurf/mcp_config.json`.",
      zh: "将以下配置添加到 `~/.codeium/windsurf/mcp_config.json`。",
    },
    verifyTitle: { en: "Verify in Cascade", zh: "在 Cascade 中验证" },
    verifyNote: {
      en: "Open Cascade MCP settings, confirm `blockrazor-docs` is connected, and enable all four tools.",
      zh: "打开 Cascade MCP 设置，确认 `blockrazor-docs` 已连接，并启用全部四个工具。",
    },
    verifyCode: null,
    instructionFile: "Cascade memories",
    showProjectInstructions: false,
  },
  {
    id: "vscode",
    name: "VS Code",
    setupTitle: { en: "Open user configuration", zh: "打开用户配置" },
    code: `{
  "servers": {
    "blockrazor-docs": {
      "type": "http",
      "url": "https://mcp.blockrazor.io/docs"
    }
  }
}`,
    note: {
      en: "In the Command Palette, run `MCP: Open User Configuration`, then add:",
      zh: "在命令面板中运行 `MCP: Open User Configuration`，然后添加：",
    },
    verifyTitle: { en: "Start and verify", zh: "启动并验证" },
    verifyNote: {
      en: "Run `MCP: List Servers`, start `blockrazor-docs`, then ask an Agent-mode question that requires current docs.",
      zh: "运行 `MCP: List Servers`，启动 `blockrazor-docs`，再在 Agent 模式中提出需要当前文档的问题。",
    },
    verifyCode: null,
    instructionFile: ".github/copilot-instructions.md",
    showProjectInstructions: false,
  },
] as const;

export const agentInstructions = {
  en: `For BlockRazor-related work, prefer the BlockRazor MCP tools over model memory.

Use \`searchDocumentation\` first for non-trivial questions so you can find the current product guide, API reference, or integration page. Use \`getPage\` when you need the complete source page before implementing exact parameters, authentication, limits, or transport behavior.

Use \`askQuestion\` for narrow how-to questions, integration decisions, errors, or debugging, and keep the returned source links in your reasoning. If documentation is outdated, incorrect, or missing required details, use \`sendFeedback\` with the relevant page URL.

The BlockRazor MCP is documentation-only. Do not treat it as a transaction-execution tool.`,
  zh: `处理 BlockRazor 相关工作时，优先使用 BlockRazor MCP 工具，而不是依赖模型记忆。

对于非简单问题，先使用 \`searchDocumentation\` 查找当前产品指南、API 参考或集成页面。实现涉及精确参数、认证、限制或传输行为的功能前，使用 \`getPage\` 读取完整来源页面。

对于具体操作、集成决策、报错或调试问题，使用 \`askQuestion\`，并在推理中保留返回的来源链接。如果文档过时、错误或缺少必要信息，使用 \`sendFeedback\` 并附上对应页面 URL。

BlockRazor MCP 仅提供文档能力，不要将它作为交易执行工具。`,
} as const;

export const toolItems = [
  {
    name: "askQuestion",
    title: { en: "Ask a direct question", zh: "直接提问" },
    description: {
      en: "Get a synthesized answer about BlockRazor with links back to the source documentation.",
      zh: "获得关于 BlockRazor 的综合答案，并返回对应文档来源链接。",
    },
  },
  {
    name: "searchDocumentation",
    title: { en: "Search documentation", zh: "搜索文档" },
    description: {
      en: "Find relevant guides, API references, code examples, and implementation details.",
      zh: "查找相关指南、API 参考、代码示例和实现细节。",
    },
  },
  {
    name: "getPage",
    title: { en: "Read a full page", zh: "读取完整页面" },
    description: {
      en: "Retrieve complete Markdown when a search excerpt is not enough.",
      zh: "当搜索摘要不够时，读取完整的 Markdown 文档。",
    },
  },
  {
    name: "sendFeedback",
    title: { en: "Improve the docs", zh: "改进文档" },
    description: {
      en: "Report documentation that is outdated, incorrect, or missing important information.",
      zh: "报告过时、错误或缺少关键信息的文档内容。",
    },
  },
] as const;

export const coverageItems = [
  {
    title: { en: "RPC", zh: "RPC" },
    body: { en: "Private routing, MEV protection, rebates, authentication, and integration guides.", zh: "隐私路由、MEV 保护、返利、认证与集成指南。" },
  },
  {
    title: { en: "Block Builder", zh: "Block Builder" },
    body: { en: "BSC block construction, submission paths, and builder integration references.", zh: "BSC 区块构建、提交通道与 Builder 集成参考。" },
  },
  {
    title: { en: "Solana Fast", zh: "Solana Fast" },
    body: { en: "Low-latency sending modes, endpoints, and testing guidance.", zh: "低延迟发送模式、端点与测试指南。" },
  },
  {
    title: { en: "Public Mempool", zh: "Public Mempool" },
    body: { en: "BSC public mempool streams, access methods, and lead-time benchmarks.", zh: "BSC 公共内存池数据流、接入方式与领先时间基准。" },
  },
  {
    title: { en: "Private Mempool", zh: "Private Mempool" },
    body: { en: "Authorized order-flow access, private routing, and integration requirements.", zh: "授权订单流访问、隐私路由与集成要求。" },
  },
  {
    title: { en: "Node Stream", zh: "Node Stream" },
    body: { en: "Low-latency full-node synchronization streams and connection guidance.", zh: "低延迟全节点同步数据流与连接指南。" },
  },
] as const;

export const prompts = [
  {
    tool: "searchDocumentation",
    title: { en: "Documentation search", zh: "文档搜索" },
    prompt: {
      en: "Find the current BlockRazor documentation for Solana sandwichMitigation mode and return the relevant setup and API pages.",
      zh: "查找 BlockRazor 当前关于 Solana sandwichMitigation 模式的文档，并返回相关设置与 API 页面。",
    },
  },
  {
    tool: "getPage",
    title: { en: "Full-page retrieval", zh: "读取完整页面" },
    prompt: {
      en: "Read the full BlockRazor Solana Send Transaction guide and summarize its modes, authentication, and rate limits.",
      zh: "阅读完整的 BlockRazor Solana Send Transaction 指南，并总结其模式、认证与速率限制。",
    },
  },
  {
    tool: "askQuestion",
    title: { en: "Integration help", zh: "集成问答" },
    prompt: {
      en: "How do I replace an existing ethers.js RPC endpoint with BlockRazor RPC? Include testing and rollout steps with source links.",
      zh: "如何将现有的 ethers.js RPC 端点替换为 BlockRazor RPC？请提供测试、上线步骤和来源链接。",
    },
  },
  {
    tool: "sendFeedback",
    title: { en: "Documentation feedback", zh: "文档反馈" },
    prompt: {
      en: "The authentication requirements on this BlockRazor documentation page appear outdated. Report the issue and include the affected section: [page URL]",
      zh: "这个 BlockRazor 文档页面中的认证要求似乎已经过时。请报告该问题，并附上受影响的章节：[页面 URL]",
    },
  },
] as const;

export const faqs = [
  {
    q: { en: "What is the BlockRazor MCP?", zh: "什么是 BlockRazor MCP？" },
    a: {
      en: "It is the official documentation knowledge endpoint for MCP-compatible coding agents. It searches BlockRazor docs, reads full pages, and returns source-linked answers.",
      zh: "它是面向 MCP 兼容编码 Agent 的官方文档知识端点，可搜索 BlockRazor 文档、读取完整页面并返回带来源的答案。",
    },
  },
  {
    q: { en: "Can it submit transactions?", zh: "它能发送交易吗？" },
    a: {
      en: "No. The current MCP exposes documentation tools only. It helps agents understand and implement BlockRazor products, but it does not execute transactions.",
      zh: "不能。当前 MCP 只提供文档工具，帮助 Agent 理解和集成 BlockRazor 产品，不执行交易。",
    },
  },
  {
    q: { en: "Do I need an API key?", zh: "是否需要 API Key？" },
    a: {
      en: "The public documentation MCP currently accepts connections without an API key. Product APIs may have separate authentication requirements.",
      zh: "公开文档 MCP 当前无需 API Key。BlockRazor 产品 API 可能有各自的认证要求。",
    },
  },
  {
    q: { en: "Which clients are supported?", zh: "支持哪些客户端？" },
    a: {
      en: "Any client that supports remote Streamable HTTP MCP servers can connect, including Codex, Claude Code, Cursor, Windsurf, and VS Code.",
      zh: "任何支持远程 Streamable HTTP MCP 服务的客户端均可连接，包括 Codex、Claude Code、Cursor、Windsurf 和 VS Code。",
    },
  },
] as const;
